// app/javascript/components/quotes/builder/components/AddGroupModal.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getRequest, pathname } from "@react/helpers/fetch"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import { useEndpoints } from "../context/EndpointsContext"
import { useGroupPicker } from "../context/GroupPickerContext"
import { formatNumber } from "../helpers/formatting"
import { findCostCategory, costCategoryVisual, OUVRAGE_VISUAL } from "@react/quotes/qline_service_blueprints/kinds"
import BlueprintTree, { ouvrageOf } from "@react/quotes/qline_service_blueprints/components/BlueprintTree"
import Button     from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import IconBadge  from "@react/shared/ui/IconBadge"
import Label      from "@react/shared/ui/Label"
import EmptyState from "@react/shared/ui/EmptyState"
import Input      from "@react/shared/ui/Input"

const AddGroupModal = () => {
  const { state, dispatch } = useQuoteEditor()
  const endpoints = useEndpoints()
  const { isOpen, close, targetSectionUid } = useGroupPicker()

  const [step, setStep] = useState("search")
  const [query, setQuery] = useState("")
  const [results, setResults] = useState([])
  const [loadingList, setLoadingList] = useState(false)
  const [group, setGroup] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [qty, setQty] = useState(1)
  const [unit, setUnit] = useState("")
  const [inserting, setInserting] = useState(false)
  // The whole row is kept, not only its id: the footer names the selection even
  // after the search changed.
  const [picked, setPicked] = useState([])
  const [folded, setFolded] = useState({})

  const searchRef = useRef(null)
  const debounceRef = useRef(null)

  const currencySymbol = state.currencySymbol || "€"

  const findSection = (nodes, uid) => {
    for (const n of nodes || []) {
      if (n._uid === uid) return n
      const nested = findSection(n.children, uid)
      if (nested) return nested
    }
    return null
  }

  const targetSection = useMemo(
    () => findSection(state.sections, targetSectionUid),
    [state.sections, targetSectionUid]
  )

  useEffect(() => {
    if (!isOpen) return
    setStep("search")
    setQuery("")
    setResults([])
    setGroup(null)
    setQty(1)
    setUnit("")
    setInserting(false)
    setPicked([])
    runSearch("")
    setTimeout(() => searchRef.current?.focus(), 60)
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = "" }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = e => { if (e.key === "Escape") close() }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isOpen, close])

  const runSearch = useCallback(q => {
    if (!endpoints.search_qline_service_blueprints) return
    setLoadingList(true)
    const url = pathname({ path: endpoints.search_qline_service_blueprints, params: q ? { q } : {} })
    getRequest(url)
      .then(data => setResults(
        (Array.isArray(data) ? data : []).map(row => ({ ...row, summary: stripTags(row.description) }))
      ))
      .finally(() => setLoadingList(false))
  }, [endpoints.search_qline_service_blueprints])

  const onQueryChange = e => {
    const v = e.target.value
    setQuery(v)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => runSearch(v), 200)
  }

  const onSelectGroup = id => {
    if (!endpoints.qline_service_blueprints_path) return
    setLoadingDetail(true)
    const url = pathname({ path: `${endpoints.qline_service_blueprints_path}/${id}`, format: "json" })
    getRequest(url)
      .then(data => {
        setGroup(data)
        setFolded({})
        setUnit(data?.unit || "")
        setQty(1)
        setStep("detail")
      })
      .finally(() => setLoadingDetail(false))
  }

  const goBackToSearch = () => {
    setStep("search")
    setTimeout(() => searchRef.current?.focus(), 50)
  }

  const stepQty = delta => setQty(v => Math.max(0.01, +(v + delta).toFixed(2)))

  // A line naming another ouvrage already arrives as a child; counting it charges twice.
  const ownLines = detail => (detail?.qline_breakdown_blueprints || []).filter(line => !line.child_blueprint)

  // Both, not one or the other: an ouvrage can hold sub-ouvrages and its own costs.
  const breakdowns = useMemo(() => {
    if (!group) return []
    return [
      ...(group.children || []).map(child => ({
        description:         child.name,
        ref_product_distrib: child.ref_product_distrib,
        cost_category:       null,
        quantity:            child.quantity,
        unit:                child.unit,
        unit_price:          child.unit_price,
        ouvrage:             true,
        children:            ouvrageOf(child).children,
      })),
      ...ownLines(group),
    ]
  }, [group])

  const compositionFigures = (node, total) => (
    <>
      <div className="flex items-center justify-end gap-1 whitespace-nowrap text-[12px] tabular-nums text-slate-700">
        {formatNumber(node.quantity)}
        <span className="text-slate-400">{node.unit}</span>
      </div>
      <div className="flex items-center justify-end whitespace-nowrap text-[12px] tabular-nums text-slate-500">
        {formatNumber(node.unit_price)} {currencySymbol}
      </div>
      <div className="flex items-center justify-end whitespace-nowrap text-[12px] tabular-nums font-semibold text-slate-900">
        {formatNumber(total)} {currencySymbol}
      </div>
    </>
  )

  const previewTotal = breakdowns.reduce(
    (s, c) => s + (c.quantity || 0) * (c.unit_price || 0),
    0,
  ) * qty

  const isPicked = id => picked.some(row => row.id === id)

  const togglePicked = row => setPicked(current =>
    current.some(item => item.id === row.id)
      ? current.filter(item => item.id !== row.id)
      : [...current, row]
  )

  const blueprintTree = (detail, quantity = 1) => ({
    name:     detail.name || detail.ref_product_distrib || "",
    ref:      detail.ref_product_distrib || "",
    unit:     detail.unit || "",
    quantity,
    children: detail.children || [],
    lines: ownLines(detail).map(line => ({
      description:         line.description || line.name || "",
      cost_category:       line.cost_category,
      quantity:            line.quantity || 0,
      unit:                line.unit || "",
      unit_price:          line.unit_price || 0,
      purchase_price:      line.purchase_price || null,
      ref_product_distrib: line.ref_product_distrib || "",
      product_id:          line.product?.id || null,
    })),
  })

  // The search list does not carry the components, so each work is fetched;
  // everything is then inserted at once, as a single undo step.
  const insertPicked = () => {
    if (inserting || picked.length === 0 || !targetSectionUid) return
    setInserting(true)

    const detailUrl = id => pathname({ path: `${endpoints.qline_service_blueprints_path}/${id}`, format: "json" })

    Promise.all(picked.map(row => getRequest(detailUrl(row.id)).catch(() => null)))
      .then(details => {
        const blueprints = details.filter(Boolean).map(detail => blueprintTree(detail))

        if (blueprints.length === 0) {
          flashAlert(I18n.t("quotes.builder.add_group_modal.insert_failed"))
          setInserting(false)
          return
        }

        dispatch({
          type:    "INSERT_BLUEPRINTS",
          payload: { parentUid: targetSectionUid, blueprints, asLines: true },
        })
        close()
      })
      .catch(() => {
        flashAlert(I18n.t("quotes.builder.add_group_modal.insert_failed"))
        setInserting(false)
      })
  }

  const doInsert = () => {
    if (inserting || !targetSectionUid || !group) return
    setInserting(true)

    const blueprint = { ...blueprintTree(group, qty || 1), unit: unit || group.unit || "" }
    dispatch({
      type:    "INSERT_BLUEPRINTS",
      payload: { parentUid: targetSectionUid, blueprints: [blueprint], asLines: true },
    })
    close()
  }

  if (!isOpen) return null

  const targetLabel = targetSection?.description
    ? stripTags(targetSection.description).slice(0, 80)
    : I18n.t("quotes.builder.add_group_modal.this_group")

  const header = (
    <div className="flex items-start justify-between gap-3 px-[22px] py-4 border-b border-slate-200">
      <div className="flex items-start gap-2.5 min-w-0">
        {step === "detail" && (
          <IconButton size="md" onClick={goBackToSearch} title={I18n.t("quotes.builder.add_group_modal.back_to_search")}>
            <FontAwesomeIcon icon="arrow-left" />
          </IconButton>
        )}
        <div className="min-w-0">
          <h2 className="m-0 flex items-center gap-2.5 text-[17px] font-semibold -tracking-[0.01em] min-w-0">
            <IconBadge tone="group" size="md">
              <FontAwesomeIcon icon="cubes" />
            </IconBadge>
            <span className="overflow-hidden text-ellipsis whitespace-nowrap">
              {step === "search"
                ? I18n.t("quotes.builder.add_group_modal.title_search")
                : (group?.name || group?.ref_product_distrib || I18n.t("quotes.builder.add_group_modal.title_detail_fallback"))}
            </span>
          </h2>
          <div className="text-[12.5px] text-slate-500 mt-1">
            {step === "search" ? (
              <>{I18n.t("quotes.builder.add_group_modal.search_eyebrow_prefix")} <strong className="text-slate-700 font-semibold">{targetLabel}</strong>.</>
            ) : (
              <>
                {group?.ref_product_distrib && <span className="font-mono">{group.ref_product_distrib}</span>}
                {group?.ref_product_distrib && " · "}
                {I18n.t("quotes.builder.add_group_modal.detail_eyebrow")}
              </>
            )}
          </div>
        </div>
      </div>
      <IconButton size="md" onClick={close} aria-label={I18n.t("quotes.builder.add_group_modal.close")}>
        <FontAwesomeIcon icon="times" />
      </IconButton>
    </div>
  )

  const backdropCls = "fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
  const modalCls    = "bg-white rounded-2xl border border-slate-200 w-[720px] max-w-[90vw] max-h-[86vh] flex flex-col overflow-hidden"

  if (step === "search") {
    return (
      <div className={backdropCls} onClick={close}>
        <div className={modalCls} onClick={e => e.stopPropagation()}>
          {header}

          <div className="px-[22px] py-3.5 border-b border-slate-200">
            <Input
              ref          = {searchRef}
              variant      = "search"
              size         = "lg"
              leadingIcon  = "search"
              type         = "text"
              value        = {query}
              onChange     = {onQueryChange}
              placeholder  = {I18n.t("quotes.builder.add_group_modal.search_placeholder")}
              autoComplete = "off"
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="py-1">
              {results.map(o => (
                <div
                  key       = {o.id}
                  onClick   = {() => onSelectGroup(o.id)}
                  className = {`grid grid-cols-[18px_36px_1fr_auto_auto] gap-3.5 items-center px-[22px] py-3 cursor-pointer border-t border-slate-200 first:border-t-0 ${
                    isPicked(o.id) ? "bg-brand-50" : "hover:bg-slate-100"
                  }`}
                >
                  {/* Cocher ne quitte pas la liste : c'est tout l'intérêt.
                      Cliquer ailleurs sur la ligne ouvre son détail, comme
                      avant, pour l'insertion d'un seul ouvrage. */}
                  <input
                    type       = "checkbox"
                    checked    = {isPicked(o.id)}
                    onChange   = {() => {}}
                    onClick    = {event => { event.stopPropagation(); togglePicked(o) }}
                    aria-label = {I18n.t("quotes.builder.add_group_modal.pick")}
                    className  = "w-3.5 h-3.5 accent-brand-500 cursor-pointer"
                  />
                  <IconBadge tone="group" size="lg">
                    <FontAwesomeIcon icon="cubes" />
                  </IconBadge>
                  <div className="min-w-0 flex flex-col gap-1">
                    <div className="font-semibold text-slate-900 text-[14px] -tracking-[0.005em] whitespace-nowrap overflow-hidden text-ellipsis">
                      {o.name || o.ref_product_distrib}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] text-slate-500">
                      {o.ref_product_distrib && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 border border-slate-200 px-1.5 py-px text-slate-700">
                          <span className="text-slate-400">{I18n.t("quotes.builder.add_group_modal.ref_label")}</span>
                          <span className="font-mono font-semibold">{o.ref_product_distrib}</span>
                        </span>
                      )}
                      {o.unit && <span>{I18n.t("quotes.builder.add_group_modal.unit_label", { unit: o.unit })}</span>}
                      {o.line_count > 0 && <span>{I18n.t("quotes.qline_service_blueprints.form.line_count", { count: o.line_count })}</span>}
                      {o.composed && (
                        <span className="inline-flex items-center gap-1 text-group-500">
                          <FontAwesomeIcon icon="cubes" />
                          {I18n.t("quotes.builder.add_group_modal.has_sub_ouvrages")}
                        </span>
                      )}
                    </div>
                    {o.summary && <div className="text-[12px] text-slate-400 line-clamp-2">{o.summary}</div>}
                  </div>
                  <div className="text-right text-[11.5px] text-slate-400 tabular-nums">
                    <div className="text-slate-900 font-semibold text-[13.5px] -tracking-[0.005em]">
                      {formatNumber((o.sale_cents || 0) / 100)} {currencySymbol}
                    </div>
                  </div>
                </div>
              ))}

              {!loadingList && results.length === 0 && (
                <EmptyState size="lg">
                  {query
                    ? I18n.t("quotes.builder.add_group_modal.no_results")
                    : I18n.t("quotes.builder.add_group_modal.empty_library")}
                </EmptyState>
              )}
              {(loadingList || loadingDetail) && results.length === 0 && (
                <EmptyState size="lg">
                  <FontAwesomeIcon icon="spinner" spin />
                </EmptyState>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 px-[22px] py-4 border-t border-slate-200 bg-white">
            <div className="flex justify-between items-center gap-2.5">
              <span className="text-[12.5px] text-slate-500">
                {picked.length > 0
                  ? I18n.t("quotes.builder.add_group_modal.picked", { count: picked.length })
                  : I18n.t("quotes.builder.add_group_modal.pick_hint")}
              </span>
              <div className="flex items-center gap-2.5">
                {picked.length > 0 && (
                  <Button variant="ghost" onClick={() => setPicked([])}>
                    {I18n.t("quotes.builder.selection.clear")}
                  </Button>
                )}
                <Button variant="secondary" onClick={close}>{I18n.t("quotes.builder.add_group_modal.cancel")}</Button>
                {picked.length > 0 && (
                  <Button variant="primary" onClick={insertPicked} disabled={inserting}>
                    <FontAwesomeIcon icon={inserting ? "spinner" : "plus"} spin={inserting} />
                    {I18n.t("quotes.builder.add_group_modal.insert_picked", { count: picked.length })}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={backdropCls} onClick={close}>
      <div className={modalCls} onClick={e => e.stopPropagation()}>
        {header}

        <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-slate-50">
          {loadingDetail && (
            <EmptyState size="lg">
              <FontAwesomeIcon icon="spinner" spin />
            </EmptyState>
          )}

          {!loadingDetail && group && (
            <>
              <div className="flex items-center justify-between px-3.5 py-2.5 bg-group-50 border border-group-500/25 rounded-[10px] mb-3.5">
                <div className="text-[12px] text-group-500 font-semibold uppercase tracking-[0.04em]">
                  {I18n.t("quotes.builder.add_group_modal.component_count", { count: breakdowns.length })}
                </div>
                <div className="text-[13.5px] text-slate-700 tabular-nums">
                  {I18n.t("quotes.builder.add_group_modal.total_excl_tax_label")}
                  <strong className="text-slate-900 font-bold text-[15px] ml-1.5">
                    {formatNumber(previewTotal)} {currencySymbol}
                  </strong>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-[10px] overflow-hidden">
                {breakdowns.length === 0 && (
                  <EmptyState size="lg">{I18n.t("quotes.builder.add_group_modal.no_components")}</EmptyState>
                )}

                {breakdowns.map((c, i) => {
                  const visual        = c.ouvrage ? OUVRAGE_VISUAL : costCategoryVisual(c.cost_category)
                  const categoryLabel = c.cost_category
                    ? (findCostCategory(c.cost_category)?.label || "—")
                    : I18n.t("quotes.qline_service_blueprints.form.component_chip")
                  const composition   = c.children || []
                  const open          = composition.length > 0 && !folded[i]
                  return (
                  <div key={i} className="flex gap-2.5 items-start px-3.5 py-3 text-[13px] text-slate-700 border-t border-slate-200 first:border-t-0 min-w-0">
                    <IconBadge tone={visual.tone} size="sm" className="mt-0.5">
                      <FontAwesomeIcon icon={visual.icon} />
                    </IconBadge>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-900 leading-[1.5] font-medium">
                        {stripTags(c.description) || categoryLabel}
                        {c.ref_product_distrib && (
                          <span className="ml-1.5 font-mono text-[11px] font-medium text-slate-400">{c.ref_product_distrib}</span>
                        )}
                        {c.description && (
                          <span className="ml-1.5 text-[11px] font-normal text-slate-400">{categoryLabel}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-baseline gap-x-[18px] gap-y-1 mt-1.5 text-[12px] text-slate-500 tabular-nums">
                        <span className="inline-flex items-baseline gap-1.5">
                          <Label size="xs">{I18n.t("quotes.builder.add_group_modal.qty")}</Label>
                          <span className="text-slate-700 font-medium">{formatNumber(c.quantity || 0)} {c.unit}</span>
                        </span>
                        <span className="inline-flex items-baseline gap-1.5">
                          <Label size="xs">{I18n.t("quotes.builder.add_group_modal.unit_price_excl_tax")}</Label>
                          <span className="text-slate-700 font-medium">{formatNumber(c.unit_price)} {currencySymbol}</span>
                        </span>
                        <span className="inline-flex items-baseline gap-1.5">
                          <Label size="xs">{I18n.t("quotes.builder.add_group_modal.total_excl_tax")}</Label>
                          <span className="text-slate-900 font-bold">{formatNumber((c.quantity || 0) * (c.unit_price || 0))} {currencySymbol}</span>
                        </span>
                        {composition.length > 0 && (
                          <button
                            type      = "button"
                            onClick   = {() => setFolded(current => ({ ...current, [i]: !current[i] }))}
                            title     = {I18n.t("quotes.builder.aggregate.toggle_collapse")}
                            className = "ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-900"
                          >
                            <FontAwesomeIcon icon="chevron-down" className={`text-[10px] transition-transform ${open ? "" : "-rotate-90"}`} />
                            {I18n.t("quotes.qline_service_blueprints.form.line_count", { count: composition.length })}
                          </button>
                        )}
                      </div>
                      {open && (
                        <div className="mt-2 rounded-lg bg-slate-50 py-1 pr-2">
                          <BlueprintTree
                            nodes         = {composition}
                            factor        = {c.quantity || 0}
                            columns       = "minmax(0, 1fr) 64px 80px 88px"
                            renderFigures = {compositionFigures}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col gap-3 px-[22px] py-4 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3.5 flex-wrap">
            <div className="flex items-center gap-2 px-2.5 py-1.5 border border-slate-200 rounded-[10px] bg-slate-100">
              <Label size="sm">{I18n.t("quotes.builder.add_group_modal.quantity")}</Label>
              <div className="inline-flex items-center gap-1 bg-white rounded-[7px] p-0.5 border border-slate-200">
                <IconButton size="xs" onClick={() => stepQty(-1)}>
                  <FontAwesomeIcon icon="minus" />
                </IconButton>
                <input
                  type      = "number"
                  value     = {qty}
                  min       = "0.01"
                  step      = "1"
                  onChange  = {e => setQty(Math.max(0.01, parseFloat(e.target.value) || 1))}
                  className = "w-[54px] text-center border-0 outline-none bg-transparent text-[13.5px] font-semibold text-slate-900 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <input
                  type        = "text"
                  value       = {unit}
                  placeholder = {I18n.t("quotes.builder.add_group_modal.unit_placeholder")}
                  onChange    = {e => setUnit(e.target.value)}
                  className   = "w-14 text-center border-0 outline-none bg-transparent text-[12.5px] text-slate-700 px-1 border-l border-slate-200 focus:text-slate-900 placeholder:text-slate-400"
                />
                <IconButton size="xs" onClick={() => stepQty(+1)}>
                  <FontAwesomeIcon icon="plus" />
                </IconButton>
              </div>
            </div>
            <div className="flex-1 min-w-[160px] text-[12px] text-slate-400">
              {I18n.t("quotes.builder.add_group_modal.quantity_hint")}
            </div>
          </div>

          <div className="flex justify-end items-center gap-2.5">
            <Button variant="secondary" onClick={goBackToSearch}>
              <FontAwesomeIcon icon="arrow-left" /> {I18n.t("quotes.builder.add_group_modal.back")}
            </Button>
            <Button variant="primary" onClick={doInsert} disabled={inserting}>
              <FontAwesomeIcon icon="plus" /> {I18n.t("quotes.builder.add_group_modal.insert")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

const stripTags = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html")
  doc.body.querySelectorAll("br").forEach(br => br.replaceWith(" "))
  doc.body.querySelectorAll("p, li").forEach(block => block.append(" "))
  return (doc.body.textContent || "").replace(/[\s\u00a0]+/g, " ").trim()
}

export default AddGroupModal
